/**
 * ProtectedSectionNode - A read-only Lexical node for protected markdown sections
 *
 * This node wraps protected sections (like "### Gebreken") to prevent accidental
 * user edits in the frontend markdown editor. The section can only be modified
 * through specialized backend tools (e.g., bevindingen_noteren).
 */

import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import type { JSX } from 'react';

import { DecoratorNode } from 'lexical';
import { ProtectedSectionComponent } from './ProtectedSectionComponent';

export type SerializedProtectedSectionNode = Spread<
  {
    heading: string;
    placeholder: string;
  },
  SerializedLexicalNode
>;

/**
 * Configuration for protected sections
 * Add new protected sections here to extend protection
 * Note: Display text (title and reason) is now retrieved from i18n translations
 */
export interface ProtectedSectionConfig {
  heading: string;
  placeholder: string;
  i18nKey: string; // Key for i18n translation (e.g., 'gebreken')
}

export const PROTECTED_SECTIONS: ProtectedSectionConfig[] = [
  {
    heading: '### Gebreken',
    placeholder: '`gebreken_tabel_via_tool`',
    i18nKey: 'gebreken',
  },
];

/**
 * ProtectedSectionNode - Custom Lexical node for read-only protected sections
 *
 * This node:
 * - Renders as a visually distinct read-only block
 * - Prevents text insertion, deletion, and modification
 * - Exports back to exact original markdown
 * - Shows tooltip explaining why it's protected
 */
export class ProtectedSectionNode extends DecoratorNode<JSX.Element> {
  __heading: string;
  __placeholder: string;

  static getType(): string {
    return 'protected-section';
  }

  static clone(node: ProtectedSectionNode): ProtectedSectionNode {
    return new ProtectedSectionNode(node.__heading, node.__placeholder, node.__key);
  }

  constructor(heading: string, placeholder: string, key?: NodeKey) {
    super(key);
    this.__heading = heading;
    this.__placeholder = placeholder;
  }

  // Serialization
  static importJSON(serializedNode: SerializedProtectedSectionNode): ProtectedSectionNode {
    return $createProtectedSectionNode(serializedNode.heading, serializedNode.placeholder);
  }

  exportJSON(): SerializedProtectedSectionNode {
    return {
      heading: this.__heading,
      placeholder: this.__placeholder,
      type: 'protected-section',
      version: 1,
    };
  }

  // DOM Conversion
  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-protected-section')) {
          return null;
        }
        return {
          conversion: () => ({
            node: $createProtectedSectionNode(),
          }),
          priority: 1,
        };
      },
    };
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-protected-section', 'true');
    element.className = 'protected-section-node';
    return element;
  }

  updateDOM(): false {
    // Decorator nodes don't update via DOM manipulation
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-protected-section', 'true');
    element.textContent = `${this.__heading}\n${this.__placeholder}`;
    return { element };
  }

  // Editing behavior - all disabled to make node read-only
  isInline(): boolean {
    return false;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }

  canBeEmpty(): boolean {
    return false;
  }

  isIsolated(): boolean {
    return true; // Prevents merging with adjacent nodes
  }

  // Render the visual component
  decorate(): JSX.Element {
    return (
      <ProtectedSectionComponent
        nodeKey={this.__key}
        heading={this.__heading}
        placeholder={this.__placeholder}
      />
    );
  }

  // Getters
  getHeading(): string {
    return this.__heading;
  }

  getPlaceholder(): string {
    return this.__placeholder;
  }

  // Text content for search/export
  getTextContent(): string {
    return `${this.__heading}\n${this.__placeholder}`;
  }
}

/**
 * Factory function to create a ProtectedSectionNode
 */
export function $createProtectedSectionNode(
  heading: string = '### Gebreken',
  placeholder: string = '`gebreken_tabel_via_tool`'
): ProtectedSectionNode {
  return new ProtectedSectionNode(heading, placeholder);
}

/**
 * Type guard to check if a node is a ProtectedSectionNode
 */
export function $isProtectedSectionNode(
  node: LexicalNode | null | undefined
): node is ProtectedSectionNode {
  return node instanceof ProtectedSectionNode;
}
